import { useCallback } from "react";

import { Box, Typography } from "@mui/material";

import { ModelType } from "../../../API";
import { MediaFullScreen } from "../../../common/components/media/MediaFullScreen";
import TableCellTruncated from "../../../common/components/table/TableCellTruncated";
import { useCustomerIdGuard } from "../../../common/hooks/useCustomerIdGuard";
import useLocationFromCache from "../../../common/hooks/useLocationFromCache";
import { UpdateEventFeedbackInput } from "../../../common/types/events/api";
import { formatTimestamp } from "../../../common/utils/timestampUtils";
import {
  errorNotification,
  successNotification,
} from "../../../common/variables/notification";
import { useSelectedLocation } from "../../../common/variables/selectedLocation";
import { useSelectedModel } from "../../../common/variables/selectedModel";
import { serviceTitleByType } from "../../data-hub/DataHubPage";
import FeedbackDetailsTableCellContainer from "../components/feedback/FeedbackDetailsTableCellContainer";
import FeedbackThumbsTableCellContainer from "../components/feedback/FeedbackThumbsTableCellContainer";
import { EventTableColumn } from "../components/table/EventTable";
import { EventViewModel } from "../models/eventViewModel";
import { RecordingViewModel } from "../models/recordingViewModel";
import { useUpdateEventFeedback } from "./events/useUpdateEventFeedback";
import { useFeedbackUpdateConfirmDialog } from "./useFeedbackUpdateConfirmDialog";

export const useBasicEventTableColumns = (
  newestRecording?: RecordingViewModel
) => {
  const customerId = useCustomerIdGuard();
  const modelId = useSelectedModel();
  const facilityId = useSelectedLocation();

  const { updateEventFeedback, loading, error } = useUpdateEventFeedback({
    customerId,
    modelId: modelId?.value ?? "",
    facilityId: facilityId?.value ?? "",
  });

  const { getCachedLocationName } = useLocationFromCache();

  const handleFeedbackUpdate = useFeedbackUpdateConfirmDialog(
    updateEventFeedback,
    error
  );

  const columns: EventTableColumn<EventViewModel>[] = [
    {
      title: "Event",
      content: event => (
        <Box
          sx={{
            display: "flex",
            width: "300px",
            alignItems: "center",
            gap: "0.75em",
            cursor: "pointer",
            position: "relative",
          }}
        >
          <Box sx={{ position: "relative" }}>
            <Box sx={{ minWidth: 70 }}>
              <MediaFullScreen
                keyFrame={newestRecording?.keyFrame}
                mediaOutput={newestRecording?.mediaOutput}
              />
            </Box>
          </Box>
          <Box sx={{ display: "flex", flexDirection: "column" }}>
            <Typography variant="buttonMedium">
              {serviceTitleByType[event.modelId.toLowerCase() as ModelType]}
            </Typography>
            <Typography variant="body2Regular" color="text.secondary">
              {formatTimestamp(event.timestamp)}
            </Typography>
          </Box>
        </Box>
      ),
    },
    {
      title: "Location",
      content: event => {
        const locationName = getCachedLocationName(event.location);

        return (
          <TableCellTruncated
            tooltipText={locationName}
            text={locationName}
            TypographyProps={{ variant: "body2" }}
          />
        );
      },
    },
    {
      title: "Feedback",
      width: "140px",
      content: event => {
        const onUpdateEventFeedback = useCallback(
          (input: UpdateEventFeedbackInput) =>
            handleFeedbackUpdate(input, event),
          [handleFeedbackUpdate, event]
        );

        return (
          <FeedbackThumbsTableCellContainer
            event={event}
            onUpdateEventFeedback={onUpdateEventFeedback}
          />
        );
      },
    },
    {
      title: "Feedback details",
      width: "980px",
      content: event => {
        const memoizedCallback = useCallback(
          async (input: UpdateEventFeedbackInput) => {
            const result = await updateEventFeedback(input);

            if (error || !result) {
              errorNotification("Something went wrong");
            } else {
              successNotification(
                "Event feedback details updated successfully"
              );
            }
          },
          [updateEventFeedback, error]
        );

        return (
          <FeedbackDetailsTableCellContainer
            loading={loading}
            event={event}
            onUpdateEventFeedback={memoizedCallback}
          />
        );
      },
    },
  ];

  return { columns };
};
